import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiUploadCloud, FiPlay, FiSquare, FiDownload, FiCopy, FiSave,
  FiRotateCcw, FiCheckCircle, FiAlertTriangle, FiLink,
} from 'react-icons/fi';
import {
  MODELS, LANGUAGES, ModelSize, TranscribeTask,
  transcribeFile, disposeTranscriber, TranscriptChunk, ProgressInfo, modelForSize,
} from '../transcribeEngine';
import { buildExport, downloadText, ExportFormat } from '../subtitles';
import { getSettings, saveToNotes, TranscribeSetting } from '../transcribeApi';
import { setFormDirty, registerSaveHandler } from '../../../lib/unsavedChanges';

type Phase = 'idle' | 'running' | 'done' | 'error';

const LS_KEY = 'akt_transcribe_prefs';
interface Prefs { size: ModelSize; language: string; task: TranscribeTask; timestamps: boolean; autosave: boolean; }
const DEFAULT_PREFS: Prefs = { size: 'small', language: 'auto', task: 'transcribe', timestamps: true, autosave: false };

function loadPrefs(): Prefs {
  try { const s = localStorage.getItem(LS_KEY); if (s) return { ...DEFAULT_PREFS, ...JSON.parse(s) }; } catch { /* noop */ }
  return DEFAULT_PREFS;
}
function fmtClock(sec: number): string {
  sec = Math.max(0, Math.round(sec)); const m = Math.floor(sec / 60);
  return `${m}:${String(sec % 60).padStart(2, '0')}`;
}

export default function TranscribeWorkbench() {
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [file, setFile] = useState<File | null>(null);
  const [sourceLink, setSourceLink] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<ProgressInfo | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [chunks, setChunks] = useState<TranscriptChunk[]>([]);
  const [durationSec, setDurationSec] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Warm from the owner's saved server defaults (fall back to local prefs on failure).
  useEffect(() => {
    let alive = true;
    getSettings().then((s: TranscribeSetting) => {
      if (!alive) return;
      setPrefs((p) => ({
        ...p,
        size: (s.model_size as ModelSize) || p.size,
        language: s.language || p.language,
        task: (s.task as TranscribeTask) || p.task,
        autosave: s.autosave_to_notes === 1 ? true : p.autosave,
      }));
    }).catch(() => { /* keep local prefs */ });
    return () => { alive = false; };
  }, []);

  // Free the Whisper worker when leaving the workbench.
  useEffect(() => () => { disposeTranscriber(); if (timerRef.current) clearInterval(timerRef.current); }, []);

  const savePrefs = useCallback((next: Partial<Prefs>) => {
    setPrefs((p) => { const merged = { ...p, ...next }; try { localStorage.setItem(LS_KEY, JSON.stringify(merged)); } catch { /* noop */ } return merged; });
  }, []);

  // A transcript that finishes after the user has left this screen must not raise
  // "unsaved changes" on the screen they are on now.
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; setFormDirty(false); }; }, []);
  const markDirty = useCallback((d: boolean) => { if (!mountedRef.current) return; setDirty(d); setFormDirty(d); }, []);

  const doSaveToNotes = useCallback(async (): Promise<boolean> => {
    const body = text.trim();
    if (!body) { setSaveMsg(''); return true; } // nothing to save is not a failure
    try {
      const model = modelForSize(prefs.size);
      const res = await saveToNotes({
        title: `Transcript — ${file?.name || sourceLink || 'recording'}`.slice(0, 120),
        transcript: body,
        sourceLink: sourceLink.trim() || undefined,
        sourceName: file?.name,
        language: LANGUAGES.find((l) => l.code === prefs.language)?.label,
        model: model.label.split(' —')[0],
        engine: 'browser',
        durationSec,
      });
      const exp = res.expires_at ? new Date(res.expires_at).toLocaleDateString() : '';
      setSaveMsg(`Saved to your Knowledge Vault${exp ? ` · auto-removes ${exp}` : ''}.`);
      markDirty(false);
      return true;
    } catch (e: any) {
      setSaveMsg(`Could not save: ${e?.message || 'error'}`);
      return false;
    }
  }, [text, prefs.size, prefs.language, file, sourceLink, durationSec, markDirty]);

  // Make the app-wide unsaved-changes modal's "Save & continue" persist to Notes.
  useEffect(() => {
    registerSaveHandler(dirty ? doSaveToNotes : null);
    return () => registerSaveHandler(null);
  }, [dirty, doSaveToNotes]);

  const pickFile = (f: File | null) => {
    if (!f) return;
    setFile(f); setError(''); setText(''); setChunks([]); setPhase('idle'); setSaveMsg(''); markDirty(false);
  };

  const start = useCallback(async () => {
    if (!file) return;
    setPhase('running'); setError(''); setText(''); setChunks([]); setSaveMsg(''); setProgress(null); setElapsed(0);
    const ac = new AbortController(); abortRef.current = ac;
    const t0 = Date.now();
    timerRef.current = setInterval(() => setElapsed((Date.now() - t0) / 1000), 500);
    try {
      const res = await transcribeFile(
        file,
        { size: prefs.size, language: prefs.language, task: prefs.task, timestamps: prefs.timestamps, signal: ac.signal },
        (p) => setProgress(p),
      );
      setText(res.text); setChunks(res.chunks); setDurationSec(res.durationSec);
      setPhase('done'); markDirty(true);
      if (prefs.autosave) { /* fire and forget; message updates on completion */ setTimeout(() => { void doSaveToNotes(); }, 0); }
    } catch (e: any) {
      if (e?.name === 'AbortError') { setPhase('idle'); }
      else { setError(e?.message || 'Transcription failed'); setPhase('error'); }
    } finally {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      abortRef.current = null;
    }
  }, [file, prefs, doSaveToNotes, markDirty]);

  const cancel = () => { abortRef.current?.abort(); };

  const onExport = (fmt: ExportFormat) => {
    const { content, mime, ext } = buildExport(fmt, text, chunks);
    const base = (file?.name || 'transcript').replace(/\.[^.]+$/, '');
    downloadText(`${base}.${ext}`, content, mime);
  };

  const onCopy = async () => {
    try { await navigator.clipboard.writeText(text); setSaveMsg('Copied to clipboard.'); }
    catch { setSaveMsg('Copy failed — select the text manually.'); }
  };

  const reset = () => {
    setFile(null); setSourceLink(''); setText(''); setChunks([]); setError(''); setSaveMsg('');
    setPhase('idle'); setProgress(null); setElapsed(0); markDirty(false);
  };

  const busy = phase === 'running';
  const pct = progress?.pct != null ? Math.round(progress.pct * 100) : null;
  const model = modelForSize(prefs.size);

  return (
    <div className="trx-workbench">
      {/* ---- Controls ---- */}
      <div className="trx-card">
        <div className="trx-controls">
          <div className="trx-field">
            <label htmlFor="trx-model">Model (accuracy vs speed)</label>
            <select id="trx-model" value={prefs.size} disabled={busy}
              onChange={(e) => savePrefs({ size: e.target.value as ModelSize })}>
              {MODELS.map((m) => <option key={m.size} value={m.size}>{m.label} · ~{m.approxMB}MB</option>)}
            </select>
            <span className="trx-hint">{model.note}</span>
          </div>
          <div className="trx-field">
            <label htmlFor="trx-lang">Language</label>
            <select id="trx-lang" value={prefs.language} disabled={busy}
              onChange={(e) => savePrefs({ language: e.target.value })}>
              {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <span className="trx-hint">Auto works well; pick Hindi/English to force it.</span>
          </div>
          <div className="trx-field">
            <label htmlFor="trx-task">Output</label>
            <select id="trx-task" value={prefs.task} disabled={busy}
              onChange={(e) => savePrefs({ task: e.target.value as TranscribeTask })}>
              <option value="transcribe">Same language (verbatim)</option>
              <option value="translate">Translate → English</option>
            </select>
            <span className="trx-hint">Translate turns Hindi speech into English text.</span>
          </div>
          <div className="trx-field">
            <label>Options</label>
            <label className="trx-check"><input type="checkbox" checked={prefs.timestamps} disabled={busy}
              onChange={(e) => savePrefs({ timestamps: e.target.checked })} /> Timestamps (for subtitles)</label>
            <label className="trx-check"><input type="checkbox" checked={prefs.autosave} disabled={busy}
              onChange={(e) => savePrefs({ autosave: e.target.checked })} /> Auto-save to Notes</label>
          </div>
        </div>
      </div>

      {/* ---- Dropzone + source link ---- */}
      <div className="trx-card">
        <div
          className={`trx-drop ${dragOver ? 'over' : ''} ${file ? 'has-file' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0] || null); }}
        >
          <FiUploadCloud className="trx-drop-icon" />
          {file ? (
            <div className="trx-file">
              <strong>{file.name}</strong>
              <span>{(file.size / 1024 / 1024).toFixed(1)} MB · not uploaded — stays on your device</span>
            </div>
          ) : (
            <div className="trx-file"><strong>Drop an audio or video file</strong><span>MP3, WAV, M4A, MP4, MOV, WebM…</span></div>
          )}
          <label className="trx-btn ghost sm">
            Choose file
            <input type="file" accept="audio/*,video/*" hidden
              onChange={(e) => pickFile(e.target.files?.[0] || null)} />
          </label>
        </div>

        <div className="trx-field" style={{ marginTop: 12 }}>
          <label htmlFor="trx-link"><FiLink style={{ verticalAlign: -2 }} /> Source link (optional — saved with the transcript, the video itself is never stored)</label>
          <input id="trx-link" type="url" placeholder="https://… (YouTube, Drive, wherever the video lives)"
            value={sourceLink} onChange={(e) => setSourceLink(e.target.value)} />
        </div>

        <div className="trx-actions">
          {!busy ? (
            <button className="trx-btn" disabled={!file} onClick={start}><FiPlay /> Transcribe</button>
          ) : (
            <button className="trx-btn danger" onClick={cancel}><FiSquare /> Stop</button>
          )}
          <button className="trx-btn ghost" onClick={reset} disabled={busy && !file}><FiRotateCcw /> Reset</button>
        </div>

        {(busy || progress) && (
          <div className="trx-progress">
            <div className="trx-progress-bar">
              <div className="trx-progress-fill" style={{ width: pct != null ? `${pct}%` : '100%', opacity: pct != null ? 1 : 0.35 }} />
            </div>
            <div className="trx-progress-label">
              {progress?.label || 'Working…'}{pct != null ? ` ${pct}%` : ''}
              {busy && <span className="trx-elapsed"> · {fmtClock(elapsed)} elapsed</span>}
            </div>
            {progress?.stage === 'download' && <div className="trx-hint">First run downloads the model once, then it’s cached for next time.</div>}
          </div>
        )}
      </div>

      {error && <div className="trx-error"><FiAlertTriangle /> {error}</div>}

      {/* ---- Result ---- */}
      {(text || chunks.length > 0) && phase !== 'running' && (
        <div className="trx-card">
          <div className="trx-result-head">
            <div>
              <strong>Transcript</strong>
              <span className="trx-hint"> · {durationSec ? fmtClock(durationSec) : ''} audio · edit freely below</span>
            </div>
            <div className="trx-export">
              <button className="trx-btn ghost sm" onClick={() => onExport('txt')}><FiDownload /> .txt</button>
              <button className="trx-btn ghost sm" onClick={() => onExport('srt')} disabled={!chunks.length} title={chunks.length ? '' : 'Enable Timestamps to export subtitles'}><FiDownload /> .srt</button>
              <button className="trx-btn ghost sm" onClick={() => onExport('vtt')} disabled={!chunks.length} title={chunks.length ? '' : 'Enable Timestamps to export subtitles'}><FiDownload /> .vtt</button>
              <button className="trx-btn ghost sm" onClick={onCopy}><FiCopy /> Copy</button>
              <button className="trx-btn sm" onClick={() => void doSaveToNotes()}><FiSave /> Save to Notes</button>
            </div>
          </div>

          <textarea className="trx-textarea" value={text}
            onChange={(e) => { setText(e.target.value); markDirty(true); }}
            spellCheck rows={12} />

          {chunks.length > 0 && (
            <details className="trx-segments">
              <summary>{chunks.length} timed segments</summary>
              <div className="trx-seg-list">
                {chunks.map((c, i) => (
                  <div className="trx-seg" key={i}>
                    <span className="trx-seg-time">{fmtClock(c.timestamp?.[0] || 0)}</span>
                    <span className="trx-seg-text">{(c.text || '').trim()}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {saveMsg && <div className="trx-savemsg"><FiCheckCircle /> {saveMsg}</div>}
        </div>
      )}
    </div>
  );
}
